import { useCallback, useEffect, useRef } from 'react';

interface UseScrollSelectedIntoViewArgs<TKey extends string | number> {
  selectedKey: TKey | undefined;
}

interface UseScrollSelectedIntoViewResult<TKey extends string | number> {
  registerElement: (key: TKey, element: HTMLElement | null) => void;
  notifyInteraction: (key: TKey) => void;
}

/**
 * Scrolls the element registered for `selectedKey` into view, once per selection and only when
 * that selection was not just reported via `notifyInteraction` (e.g. a deep-linked selection
 * present when the caller mounts, rather than an element the user just interacted with directly).
 * A `selectedKey` with no registered element is silently skipped.
 */
export const useScrollSelectedIntoView = <TKey extends string | number>({
  selectedKey,
}: UseScrollSelectedIntoViewArgs<TKey>): UseScrollSelectedIntoViewResult<TKey> => {
  const elementsRef = useRef(new Map<TKey, HTMLElement>());
  const justInteractedKeyRef = useRef<TKey | null>(null);
  const lastHandledKeyRef = useRef<TKey | null>(null);

  const registerElement = useCallback((key: TKey, element: HTMLElement | null) => {
    if (element) {
      elementsRef.current.set(key, element);
    } else {
      elementsRef.current.delete(key);
    }
  }, []);

  const notifyInteraction = useCallback((key: TKey) => {
    justInteractedKeyRef.current = key;
  }, []);

  useEffect(() => {
    if (selectedKey === undefined) {
      lastHandledKeyRef.current = null;
      return;
    }

    if (selectedKey === lastHandledKeyRef.current) {
      return;
    }

    lastHandledKeyRef.current = selectedKey;

    if (justInteractedKeyRef.current === selectedKey) {
      justInteractedKeyRef.current = null;
      return;
    }

    elementsRef.current.get(selectedKey)?.scrollIntoView({ block: 'nearest' });
  }, [selectedKey]);

  return { registerElement, notifyInteraction };
};
